import React from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faFileContract, faFileInvoice, faTruck, faFile, faUserShield, faReceipt, faCalculator, faBuilding, faListAlt } from '@fortawesome/free-solid-svg-icons';
import './TradeOpsDeleteModal.css';

function TradeOpsDeleteModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  itemType, // 'deal', 'recap', 'delivery', 'document', 'grouping', 'invoice', or 'budget'
  itemDetails, // object with relevant details
  isWarning = false, // if true, shows warning mode (no delete button)
  warningMessage = '', // custom warning message
  isDeleting = false // if true, shows loading overlay
}) {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    // Don't close here - let the parent component close after deletion completes
  };

  const handleCancel = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  // Get appropriate icon based on item type
  const getIcon = () => {
    switch (itemType) {
      case 'deal':
        return faFileContract;
      case 'recap':
        return faFileInvoice;
      case 'delivery':
        return faTruck;
      case 'document':
        return faFile;
      case 'grouping':
        return faUserShield;
      case 'invoice':
        return faReceipt;
      case 'budget':
        return faCalculator;
      case 'entity':
        return faBuilding;
      case 'master_data':
        return faListAlt;
      default:
        return faFileContract;
    }
  };

  // Get appropriate title based on item type
  const getTitle = () => {
    if (isWarning) {
      switch (itemType) {
        case 'deal':
          return 'Cannot Delete Deal';
        case 'recap':
          return 'Cannot Delete Recap';
        case 'delivery':
          return 'Cannot Delete Delivery';
        case 'document':
          return 'Cannot Delete Document';
        case 'grouping':
          return 'Cannot Deactivate Grouping';
        case 'invoice':
          return 'Cannot Delete Invoice';
        case 'budget':
          return 'Cannot Delete Budget';
        case 'entity':
          return 'Cannot Delete Entity';
        case 'master_data':
          return `Cannot Delete ${itemDetails?.recordType || 'Record'}`;
        default:
          return 'Cannot Delete Item';
      }
    }
    
    switch (itemType) {
      case 'deal':
        return 'Delete Deal';
      case 'recap':
        return 'Delete Trade Recap';
      case 'delivery':
        return 'Delete Delivery';
      case 'document':
        return 'Delete Document';
      case 'grouping':
        return 'Deactivate Access Grouping';
      case 'invoice':
        return 'Delete Invoice';
      case 'budget':
        return 'Delete Budget';
      case 'entity':
        return 'Delete Entity';
      case 'master_data':
        return `Delete ${itemDetails?.recordType || 'Record'}`;
      default:
        return 'Delete Item';
    }
  };

  // Get appropriate description based on item type
  const getDescription = () => {
    switch (itemType) {
      case 'deal':
        return 'This will permanently delete the deal and all associated data.';
      case 'recap':
        return 'This will permanently delete the trade recap and all associated data.';
      case 'delivery':
        return 'This will permanently delete the delivery and all associated data.';
      case 'document':
        return 'This will permanently delete the document from the system.';
      case 'grouping':
        return 'This will deactivate the access grouping. All access restrictions for its entities will be removed.';
      case 'invoice':
        return 'This will permanently delete the invoice and all associated data.';
      case 'budget':
        return 'This will permanently delete the budget and all associated line items.';
      case 'entity':
        return 'This will permanently delete the corporate entity and all its configuration.';
      case 'master_data':
        return `This will permanently delete the ${(itemDetails?.recordType || 'record').toLowerCase()} from the system.`;
      default:
        return 'This will permanently delete the item.';
    }
  };

  // Rendered at the top of the page, not where the host puts it. A host
  // column that stays put while the page scrolls (the delivery's paperwork
  // column) keeps everything inside it on its own layer, so a pop-up left
  // there sits UNDER the delivery's date boxes however high it is raised.
  return createPortal(
    <div className="trade-ops-delete-modal-overlay" onClick={handleCancel}>
      <div className="trade-ops-delete-modal" onClick={(e) => e.stopPropagation()}>
        {/* Loading Overlay */}
        {isDeleting && (
          <div className="trade-ops-delete-loading-overlay">
            <div className="trade-ops-delete-spinner"></div>
            <p className="trade-ops-delete-loading-text">{itemType === 'grouping' ? 'Deactivating...' : 'Deleting...'}</p>
          </div>
        )}
        
        {/* Modal Header */}
        <div className="trade-ops-delete-modal-header">
          <div className="trade-ops-delete-header-content">
            <h3 className="trade-ops-delete-modal-title">
              {getTitle()}
            </h3>
          </div>
          <button className="trade-ops-delete-close-btn" onClick={handleCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="trade-ops-delete-modal-body">
          {/* Warning Banner */}
          <div className="trade-ops-delete-warning-banner">
            <FontAwesomeIcon icon={faExclamationTriangle} className="trade-ops-delete-warning-icon" />
            <div className="trade-ops-delete-warning-text">
              <strong>{isWarning ? 'Cannot Delete' : (itemType === 'grouping' ? 'Deactivation Warning' : 'Permanent Deletion Warning')}</strong>
              <p>{isWarning ? warningMessage : `${getDescription()} This action cannot be undone.`}</p>
            </div>
          </div>

          {!isWarning && (
            <div className="trade-ops-delete-confirm-message">
              <p className="trade-ops-delete-question">
                Are you sure you want to {itemType === 'grouping' ? 'deactivate' : 'delete'} this {itemType === 'grouping' ? 'access grouping' : itemType}?
              </p>
            </div>
          )}

          {/* Item Details Card */}
          {itemDetails && (
            <div className="trade-ops-delete-details-card">
              <div className="trade-ops-delete-details-icon">
                <FontAwesomeIcon icon={getIcon()} />
              </div>
              <div className="trade-ops-delete-details-content">
                <h4 className="trade-ops-delete-details-title">
                  {itemType === 'deal' ? 'Deal' : 
                   itemType === 'recap' ? 'Trade Recap' : 
                   itemType === 'document' ? 'Document' : 
                   itemType === 'grouping' ? 'Access Grouping' :
                   itemType === 'invoice' ? 'Invoice' :
                   itemType === 'budget' ? 'Budget' :
                   itemType === 'entity' ? 'Entity' :
                   itemType === 'master_data' ? (itemDetails?.recordType || 'Record') :
                   'Delivery'} Details
                </h4>
                
                {itemDetails.entityName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Entity Name</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.entityName}</div>
                  </div>
                )}

                {itemDetails.recordName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Name</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.recordName}</div>
                  </div>
                )}

                {itemDetails.recordCode && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Code</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.recordCode}</div>
                  </div>
                )}

                {itemDetails.transactionNumber && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Transaction Number</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.transactionNumber}</div>
                  </div>
                )}

                {itemDetails.transactionType && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Transaction Type</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.transactionType}</div>
                  </div>
                )}

                {itemDetails.deliveryNumber && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Delivery Number</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.deliveryNumber}</div>
                  </div>
                )}

                {itemDetails.vesselName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Vessel</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.vesselName}</div>
                  </div>
                )}

                {itemDetails.filename && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">File Name</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.filename}</div>
                  </div>
                )}

                {itemDetails.documentType && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Document Type</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.documentType}</div>
                  </div>
                )}

                {itemDetails.dateUploaded && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Date Uploaded</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.dateUploaded}</div>
                  </div>
                )}

                {itemDetails.createdAt && itemDetails.createdAt !== 'N/A' && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Created At</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.createdAt}</div>
                  </div>
                )}

                {itemDetails.groupingName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Grouping Name</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.groupingName}</div>
                  </div>
                )}

                {itemDetails.mainOperator && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Main Operator</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.mainOperator}</div>
                  </div>
                )}

                {itemDetails.memberCount !== undefined && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Members</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.memberCount}</div>
                  </div>
                )}

                {itemDetails.invoiceNumber && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Invoice Number</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.invoiceNumber}</div>
                  </div>
                )}

                {itemDetails.supplierName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Supplier</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.supplierName}</div>
                  </div>
                )}

                {itemDetails.grossAmount && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Gross Amount</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.grossAmount}</div>
                  </div>
                )}

                {itemDetails.budgetName && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Budget Name</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.budgetName}</div>
                  </div>
                )}

                {itemDetails.currency && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Currency</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.currency}</div>
                  </div>
                )}

                {itemDetails.quantity && (
                  <div className="trade-ops-delete-detail-row">
                    <div className="trade-ops-delete-detail-label">Quantity</div>
                    <div className="trade-ops-delete-detail-value">{itemDetails.quantity}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Impact Notice */}
          {!isWarning && (
            <div className="trade-ops-delete-impact-notice">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <div className="trade-ops-delete-impact-text">
                <strong>What happens next:</strong>
                {itemType === 'grouping' ? (
                  <ul>
                    <li>The access grouping will be deactivated</li>
                    <li>All entities will become visible to all users again</li>
                    <li>Member access restrictions will no longer apply</li>
                    <li>An audit log entry will be created for record-keeping</li>
                  </ul>
                ) : (
                  <ul>
                    <li>The {itemType} will be permanently deleted from the system</li>
                    <li>All associated data will be removed</li>
                    <li>This action cannot be undone or reversed</li>
                    <li>An audit log entry will be created for record-keeping</li>
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="trade-ops-delete-modal-footer">
          {isWarning ? (
            <button 
              className="trade-ops-delete-btn-secondary" 
              onClick={handleCancel}
              disabled={isDeleting}
            >
              Close
            </button>
          ) : (
            <>
              <button 
                className="trade-ops-delete-btn-secondary" 
                onClick={handleCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="trade-ops-delete-btn-danger" 
                onClick={handleConfirm}
                disabled={isDeleting}
              >
                {itemType === 'grouping' ? 'Yes, Deactivate Grouping' : 
                  `Yes, Delete ${itemType === 'deal' ? 'Deal' : 
                               itemType === 'recap' ? 'Recap' : 
                               itemType === 'document' ? 'Document' : 
                               itemType === 'invoice' ? 'Invoice' :
                               itemType === 'budget' ? 'Budget' :
                               itemType === 'entity' ? 'Entity' :
                               itemType === 'master_data' ? (itemDetails?.recordType || 'Record') :
                               'Delivery'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default TradeOpsDeleteModal;

